import React, { useState } from 'react';
import { Heart, Eye, Activity, Brain, Sparkles, Users, Stethoscope, Bone, Baby, UserCheck, User, MoreHorizontal } from 'lucide-react';
import { BookingModal } from '@/components/BookingModal';

export const Departments = () => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [bookingType, setBookingType] = useState('department');

  const departments = [
    {
      name: 'Diabetologist',
      icon: Activity,
      description: 'Diabetes care & management',
      testId: 'department-card-diabetologist',
      gradient: 'from-blue-500/10 to-cyan-500/10',
      iconColor: 'text-blue-600',
    },
    {
      name: 'Ophthalmologist',
      icon: Eye,
      description: 'Eye care & vision health',
      testId: 'department-card-ophthalmologist',
      gradient: 'from-green-500/10 to-emerald-500/10',
      iconColor: 'text-green-600',
    },
    {
      name: 'Cardiologist',
      icon: Heart,
      description: 'Heart care & cardiovascular health',
      testId: 'department-card-cardiologist',
      gradient: 'from-rose-500/10 to-pink-500/10',
      iconColor: 'text-rose-600',
    },
    {
      name: 'Neurologist',
      icon: Brain,
      description: 'Brain & nervous system care',
      testId: 'department-card-neurologist',
      gradient: 'from-purple-500/10 to-indigo-500/10',
      iconColor: 'text-purple-600',
    },
    {
      name: 'Dermatologist',
      icon: Sparkles,
      description: 'Skin, hair & beauty treatments',
      testId: 'department-card-dermatologist',
      gradient: 'from-pink-500/10 to-rose-500/10',
      iconColor: 'text-pink-600',
    },
    {
      name: 'Gynecologist',
      icon: Users,
      description: "Women's health & wellness",
      testId: 'department-card-gynecologist',
      gradient: 'from-fuchsia-500/10 to-purple-500/10',
      iconColor: 'text-fuchsia-600',
    },
    {
      name: 'Psychologist',
      icon: User,
      description: 'Mental health & counseling',
      testId: 'department-card-psychologist',
      gradient: 'from-teal-500/10 to-cyan-500/10',
      iconColor: 'text-teal-600',
    },
    {
      name: 'Orthopedics',
      icon: Bone,
      description: 'Bone, joint & muscle treatment',
      testId: 'department-card-orthopedics',
      gradient: 'from-amber-500/10 to-orange-500/10',
      iconColor: 'text-amber-600',
    },
    {
      name: 'Pediatrics (Child Specialist)',
      icon: Baby,
      description: 'Child health & development',
      testId: 'department-card-pediatrics',
      gradient: 'from-sky-500/10 to-blue-500/10',
      iconColor: 'text-sky-600',
    },
    {
      name: 'Geriatrics',
      icon: UserCheck,
      description: 'Elderly care & wellness',
      testId: 'department-card-geriatrics',
      gradient: 'from-indigo-500/10 to-violet-500/10',
      iconColor: 'text-indigo-600',
    },
    {
      name: 'Urologist',
      icon: Stethoscope,
      description: 'Urinary system & kidney care',
      testId: 'department-card-urologist',
      gradient: 'from-cyan-500/10 to-teal-500/10',
      iconColor: 'text-cyan-600',
    },
    {
      name: 'Others',
      icon: MoreHorizontal,
      description: 'ECG, BP Check, Injection, Vaccines',
      testId: 'department-card-others',
      gradient: 'from-slate-500/10 to-gray-500/10',
      iconColor: 'text-slate-600',
      pricing: 'ECG (Center: ₹350 | Home: ₹700)',
    },
  ];

  const handleDepartmentClick = (deptName) => {
    setSelectedDepartment(deptName);
    if (deptName === 'Others') {
      setBookingType('others');
    } else {
      setBookingType('department');
    }
    setShowBookingModal(true);
  };

  return (
    <>
      <section
        id="departments"
        data-testid="departments-section"
        className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white"
      >
        <div className="container mx-auto max-w-7xl">
          {/* Section Header */}
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Our{' '}
              <span className="bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                Medical Departments
              </span>
            </h2>
            <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Comprehensive healthcare services across multiple specialties with experienced medical
              professionals
            </p>
          </div>

          {/* Departments Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
            {departments.map((dept) => {
              const IconComponent = dept.icon;
              return (
                <button
                  key={dept.testId}
                  data-testid={dept.testId}
                  onClick={() => handleDepartmentClick(dept.name)}
                  className={`group relative bg-gradient-to-br ${dept.gradient} border border-slate-200/80 rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_32px_rgba(233,30,99,0.12)] transition-all duration-300 hover:border-rose-300 hover:-translate-y-1 text-left w-full`}
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div
                      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                    >
                      <IconComponent className={`w-8 h-8 sm:w-10 sm:h-10 ${dept.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2">
                        {dept.name}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed mb-1">{dept.description}</p>
                      {dept.name === 'Others' && (
                        <p className="text-xs font-semibold text-rose-600">ECG (Center: ₹350 | Home: ₹700)</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        selectedService={selectedDepartment}
        bookingType={bookingType}
      />
    </>
  );
};